// ActivityCommentAttachments.jsx

import React, { useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { normalizeCommentAttachment } from 'node/activity/utils/commentAttachments';
import {
  isNodeMediaTypeKey,
  normalizeNodeMediaTypeKey,
} from 'node/highlight/NodeMediaHighlight';

// CSS
import './ActivityCommentAttachments.css';

// Components
import ActivityRelationshipChip from 'node/activity/items/ActivityRelationshipChip';
import DocumentIcon from 'svg/DocumentIcon';
import Icon from 'svg/Icon';

/**
 * Build a fake activity event so we can reuse ActivityRelationshipChip.
 *
 * @param {object} attachment
 * @returns {object}
 */
function attachmentToChipEvent(attachment) {
  return {
    relatedNode: {
      key: attachment.nodeKey,
      title: attachment.title || 'Attachment',
      type: attachment.type,
      // Prefer original / storagePath so the chip can render before derivatives exist.
      url: attachment.url || attachment.storagePath || null,
      url_thumb: attachment.url_thumb || null,
      url_grid: attachment.url_grid || null,
      storagePath: attachment.storagePath || attachment.url || null,
    },
    changes: [{
      changeKind: 'edge_linked',
      relatedNodeKey: attachment.nodeKey,
      relatedNodeTitle: attachment.title,
      relatedNodeType: attachment.type,
      relatedNodeUrl: attachment.url || attachment.storagePath || null,
      relatedNodeUrlThumb: attachment.url_thumb || null,
      relatedNodeUrlGrid: attachment.url_grid || null,
      relatedNodeStoragePath: attachment.storagePath || attachment.url || null,
    }],
  };
}

/**
 * Local draft preview (not yet a related node / no Peek open).
 */
function LocalDraftChip({ attachment }) {
  const { theme } = useTheme();
  const mediaType = normalizeNodeMediaTypeKey(attachment?.type);
  const showThumb = mediaType === 'PHOTO' || mediaType === 'VIDEO';
  const label = attachment?.title || 'Attachment';

  return (
    <div
      className={[
        'activity-comment-attachment-local',
        showThumb ? 'has-thumbnail' : '',
      ].filter(Boolean).join(' ')}
      title={label}
    >
      {showThumb && attachment.url_thumb ? (
        <img
          alt=""
          className="activity-comment-attachment-local-thumb"
          src={attachment.url_thumb}
          draggable={false}
        />
      ) : (
        <span
          className="activity-comment-attachment-local-file"
          style={{ color: theme.foreground }}
        >
          <DocumentIcon width={18} height={18} />
          <span className="activity-comment-attachment-local-title">{label}</span>
        </span>
      )}
    </div>
  );
}

/**
 * ActivityCommentAttachments
 *
 * Renders attachment chips under a comment/reply body (posted) or above the
 * composer (draft, with optional remove).
 *
 * @param {object[]} [attachments]
 * @param {boolean} [deleted=false] - Soft-deleted comments hide chips.
 * @param {boolean} [removable=false] - Draft mode: show remove control.
 * @param {function} [onRemove] - (draftIdOrNodeKey) => void
 * @returns {ReactNode}
 */
const ActivityCommentAttachments = React.memo(({
  attachments = [],
  deleted = false,
  removable = false,
  onRemove,
}) => {
  const rows = useMemo(() => (
    (Array.isArray(attachments) ? attachments : [])
      .map((entry) => normalizeCommentAttachment(entry))
      .filter(Boolean)
  ), [attachments]);

  if (deleted || rows.length === 0) return null;

  return (
    <div className="activity-comment-attachments">
      {rows.map((attachment) => {
        const rowKey = attachment.draftId || attachment.nodeKey;
        const useLocal = removable && (
          attachment.localPreview
          || !attachment.nodeKey
          || (attachment.url_thumb && String(attachment.url_thumb).startsWith('blob:'))
        );
        const canOpenRelated = Boolean(attachment.nodeKey)
          && isNodeMediaTypeKey(attachment.type);

        return (
          <div
            className={[
              'activity-comment-attachment-row',
              removable ? 'is-removable' : '',
            ].filter(Boolean).join(' ')}
            key={rowKey}
          >
            {useLocal || !attachment.nodeKey ? (
              <LocalDraftChip attachment={attachment} />
            ) : canOpenRelated || attachment.nodeKey ? (
              <ActivityRelationshipChip event={attachmentToChipEvent(attachment)} />
            ) : (
              <LocalDraftChip attachment={attachment} />
            )}
            {removable ? (
              <button
                className="activity-comment-attachment-remove"
                type="button"
                aria-label="Remove attachment"
                title="Remove"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onRemove?.(rowKey);
                }}
              >
                <Icon iconKey="close" width={14} height={14} />
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
});

ActivityCommentAttachments.displayName = 'ActivityCommentAttachments';

export default ActivityCommentAttachments;
